"use client";

import * as React from "react";
import type { CSSProperties } from "react";
import { BookOpen } from "lucide-react";
import { Link } from "@/i18n/routing";
import { ROUTES } from "@/lib/routes";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { PublicationDateButton } from "./publication-date-button";
import { today } from "./date-picker";

/**
 * The 出版日 + 揭頁版 pair from the listing header.
 *
 * Lives in its own client module rather than in `category-header.tsx`: that file
 * is a SERVER component and several callers pass it a `NextLink` component as a
 * prop. Marking it `"use client"` to get `useState` here broke every page that
 * does — "Functions cannot be passed directly to Client Components". Only the
 * stateful pair needs the client boundary.
 */
function iso(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * 揭頁版 — opens the e-paper for the date chosen in the sibling 出版日 picker.
 *
 * Was a bare `<button>` with no handler: QA "1. 這個按鈕沒有反應 2. 而且懸停時
 * 也沒有變成手指圖示". It is a `<Link>` now, so it gets the base pointer cursor
 * and a real destination, and it carries `?date=` so the reader lands on the
 * issue they picked rather than always on today's.
 */
function PageFlipButton({ date }: { date: Date }) {
  return (
    <Link
      href={`${ROUTES.ePaper}?date=${iso(date)}`}
      aria-label={`切換至揭頁版，${iso(date)}`}
      title="揭頁版"
      // QA: no background change on hover — the label recolours instead.
      className="inline-flex items-center gap-[10px] group rounded-[3px] px-[6px] py-[4px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{
        // @ts-expect-error custom ring color via CSS var
        "--tw-ring-color": CORE_COLORS.brandGreen,
      }}
    >
      <span
        className="inline-flex items-center justify-center shrink-0"
        style={{
          width: 32,
          height: 32,
          borderRadius: 3,
          backgroundColor: "#f5f7f6",
          color: CORE_COLORS.brandGreen,
        }}
      >
        <BookOpen size={14} strokeWidth={1.75} />
      </span>
      <span
        className="transition-colors duration-150 group-hover:text-[color:var(--subscribe-green)]"
        style={
          {
            color: CORE_COLORS.textPrimary,
            fontFamily: CORE_FONT.sans,
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: "-0.224px",
            lineHeight: "18px",
            "--subscribe-green": CORE_COLORS.subscribeGreen,
          } as CSSProperties
        }
      >
        揭頁版
      </span>
    </Link>
  );
}

export function HeaderControls() {
  // Both controls read the same date: the picker sets it, 揭頁版 links to it.
  const [date, setDate] = React.useState<Date>(() => today());
  return (
    <div className="flex items-center gap-[12px]">
      <PublicationDateButton value={date} onChange={setDate} />
      <span
        aria-hidden
        className="inline-block"
        style={{
          width: 1,
          height: 24,
          backgroundColor: CORE_COLORS.dividerSoft,
        }}
      />
      <PageFlipButton date={date} />
    </div>
  );
}

